import { isFulfilled } from "@reduxjs/toolkit";
import { endOfDay, getTime, set } from "date-fns";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { validateDate } from "../../../../app/common";
import { DEFAULT_DRAG_HANDLE_SELECTOR } from "../../../../app/constants";
import { useAppDispatch } from "../../../../app/hooks";
import BasicDialog from "../../../../common/components/functional/dialogs/BasicDialog";
import { SupplementsData } from "../../../supplements/supplementAPI";
import { fetchAssignmentsByTalentId } from "../../../talentProfile/Employment/AssignmentAdditionalInfo/AssignmentsSlice";
import { AssignmentById } from "../../../talents/talentAPI";
import { EditEntry, Supplement } from "../../entriesAPI";
import { NewEntryBasic } from "../../types";
import NewEntryDialogContent from "./NewEntryDialogContent";

type Props = {
  open: boolean;
  toggleNewEntryDialogOpen: (open: boolean) => void;
  talentId: string;
  setNewEntry: (val: EditEntry) => void;
};

const NewEntryDialog = ({
  open,
  toggleNewEntryDialogOpen,
  talentId,
  setNewEntry: setSelectedEditableEntry,
}: Props) => {
  const [assignmentOptions, setAssignmentOptions] = useState<AssignmentById[]>(
    [],
  );

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const defaultValues: NewEntryBasic = {
    startDate: null,
    assignmentId: "",
  };

  const methods = useForm<NewEntryBasic>({ defaultValues, mode: "onChange" });
  const { handleSubmit, watch } = methods;

  const startDate = watch("startDate");
  const assignmentId = watch("assignmentId");
  const isValidDate = startDate && validateDate(startDate, "dd.MM.yyyy");

  useEffect(() => {
    if (startDate && isValidDate) {
      // Fetch assignments, which are ongoing on given date
      const assignmentOngoingTimestamp = getTime(endOfDay(startDate));

      const params = {
        talentId,
        timestamp: assignmentOngoingTimestamp,
      };
      dispatch(fetchAssignmentsByTalentId(params)).then((action) => {
        if (isFulfilled(action)) {
          setAssignmentOptions(
            action.payload.sort((a, b) => a.details.start - b.details.start),
          );
        }
      });
    }
  }, [dispatch, talentId, startDate, isValidDate]);

  const handleContinue = (data: NewEntryBasic) => {
    if (data.startDate && data.assignmentId) {
      const selectedAssignment = assignmentOptions.find(
        (assignment) => assignment.assignmentId === data.assignmentId,
      );

      if (selectedAssignment) {
        const convertSupplementsData = (
          supplementsData: SupplementsData,
        ): Supplement[] => {
          return Object.keys(supplementsData)
            .filter(
              (key) =>
                supplementsData[key].mode === "DEFAULT_ON" ||
                supplementsData[key].mode === "FORCE",
            )
            .map((key) => ({
              code: key,
            }));
        };

        const assignmentTransformedToEntry: EditEntry = {
          id: "",
          created: Date.now(),
          assignmentId: selectedAssignment.assignmentId,
          approvalState: "New",
          ...getWorkTimeEntryPeriod(selectedAssignment, data.startDate),
          version: 0,
          customerOrgId: selectedAssignment.details.customerOrgId,
          customerOrgName: selectedAssignment.details.customerOrgName,
          customerCostCenterId: "",
          customerCostCenterName: "",
          leaveId: null,
          type: "WORK_ENTRY",
          defaultLunchBreak: null,
          lunchBreak: null,
          plannedStart: selectedAssignment.details.start,
          feedback: null,
          talentId,
          talentFirstName: selectedAssignment.details.talentFirstName,
          talentLastName: selectedAssignment.details.talentLastName,
          expenses: {},
          isBillable: true,
          workLocationName: selectedAssignment.details.workLocationName,
          workLocationId: selectedAssignment.details.workLocationId,
          workTitleId: selectedAssignment.details.workTitleId,
          approvalStateChanges: [],
          entryChanges: [],
          supplements: convertSupplementsData(selectedAssignment.supplements),
        };

        setSelectedEditableEntry(assignmentTransformedToEntry);
        toggleNewEntryDialogOpen(false);
      }
    }
  };

  const confirmDisabled = !isValidDate || !assignmentId;

  return (
    <BasicDialog
      open={open}
      onCancel={() => toggleNewEntryDialogOpen(false)}
      onConfirm={handleSubmit(handleContinue)}
      title={t("hourEntries:newWorkTimeEntry")}
      dialogContentText={t("hourEntries:selectDateAndAssignment")}
      confirmLabel={t("common:continue")}
      PaperProps={{
        sx: { minWidth: "572px" },
        className: DEFAULT_DRAG_HANDLE_SELECTOR,
      }}
      confirmDisabled={confirmDisabled}
      noBorder
      content={
        <FormProvider {...methods}>
          <NewEntryDialogContent assignmentOptions={assignmentOptions} />
        </FormProvider>
      }
    />
  );
};

function getWorkTimeEntryPeriod(
  assignment: AssignmentById,
  workTimeEntryDate: Date,
): { start: number; end: number } {
  if (isLongAssignment(assignment)) {
    // Long assignment duration is day(s), so lets adjust start and end date suitable for work time entry, which is less than 24 hours
    const start = set(workTimeEntryDate, {
      hours: 8,
      minutes: 0,
      seconds: 0,
    }).getTime();
    const eightHours = 1000 * 60 * 60 * 8;
    const end = start + eightHours;
    return { start, end };
  } else {
    // Short assignment is shorter than 24 hours, so we can use these as WorkTimeEntry period
    return { start: assignment.details.start, end: assignment.details.end! };
  }
}

function isLongAssignment(assignment: AssignmentById): boolean {
  // If the end date is null or not provided, consider it as a continuous/long assignment
  if (!assignment.details.end) {
    return true; // Continuous assignment (considered long)
  }

  const durationInMs = assignment.details.end - assignment.details.start;
  const durationInHours = durationInMs / 1000 / 60 / 60;

  return durationInHours > 24; // Longer than 24 hours is considered long
}

export default NewEntryDialog;
